import { useContext } from 'react';
import { css } from '@emotion/react';

import { Button } from 'components';
import UnfinishedTutorialDialog from 'components/UserBanner/UnfinishedTutorialDialog';

import { GameDifficulty, GameTypeEnum } from 'graphql/types';

import { ScenesProgressContext } from 'pages/Lobby/ScenesProgressProvider';
import { StartTrainingContext } from 'pages/Lobby/LobbyGame/StartTrainingProvider';

interface Props {
  disabled: boolean;
  creating: boolean;
  gameType: GameTypeEnum;
  selectedSceneId?: string;
  opponentCount?: number;
  startCpuGame: () => void;
  handleStartMatchmaking: () => void;
  gameDifficulty: GameDifficulty;
}

const StartGameButtonsWithoutTraining = ({
  handleStartMatchmaking,
  startCpuGame,
  disabled,
  creating,
  gameType,
  selectedSceneId,
  gameDifficulty,
  opponentCount,
}: Props) => {
  const { trainingRequirements, allScenesCompleted, trainingScenes } =
    useContext(ScenesProgressContext);

  const {
    unfinishedTutorial,
    startingTutorial,
    creatingTutorial,
    continueTutorial,
    restartTutorial,
    closeUnfinishedTutorialDialog,
  } = useContext(StartTrainingContext);

  let againstPlayerLabelT = {
    key: 'lobby.trainWithPlayer',
    defaultValue: 'Trénovat s kolegou',
  };
  let againstCpuLabelT = {
    key: 'lobby.trainWithCpu',
    defaultValue: 'Trénovat s počítačem',
  };

  const isEasyGame = gameDifficulty === GameDifficulty.EASY;

  if (
    gameType === GameTypeEnum.TRAINING_GAME &&
    trainingRequirements &&
    !allScenesCompleted
  ) {
    const { finishAgainstPlayer } = trainingRequirements;
    const scene = trainingScenes.find((ts) => ts.id === selectedSceneId);
    const sceneInCompleted = !scene?.progress?.alreadyFinished;
    if (selectedSceneId == null || sceneInCompleted) {
      againstCpuLabelT = {
        key: 'lobby.playWithCpu',
        defaultValue: 'Hrát s počítačem',
      };
      if (finishAgainstPlayer) {
        againstPlayerLabelT = {
          key: 'lobby.playWithPlayer',
          defaultValue: 'Hrát s kolegou',
        };
      }
    }
  }

  const isTournament = gameType === GameTypeEnum.TOURNAMENT_GAME;
  if (isTournament) {
    againstPlayerLabelT = {
      key: 'lobby.tournament.playWithPlayer',
      defaultValue: 'Vyzvat náhodného kolegu',
    };
  }

  const buttonDisabled =
    disabled || creating || startingTutorial || creatingTutorial;

  const buttonNodes = [];
  if (!isEasyGame) {
    buttonNodes.push(
      <Button
        key="againstPlayer"
        labelT={againstPlayerLabelT}
        onClick={() => {
          handleStartMatchmaking();
        }}
        disabled={buttonDisabled || !opponentCount}
        variant="primary"
        css={ButtonCss}
      />,
    );
  }
  if (!isTournament) {
    buttonNodes.push(
      <Button
        key="againstCpu"
        labelT={againstCpuLabelT}
        disabled={buttonDisabled}
        onClick={startCpuGame}
        variant="primary"
        css={ButtonCss}
      />,
    );
  }

  const containerCss = getContainerCss(gameType, buttonNodes.length);
  return (
    <>
      <div css={containerCss}>{buttonNodes}</div>
      <UnfinishedTutorialDialog
        isOpen={unfinishedTutorial != null}
        onClose={closeUnfinishedTutorialDialog!}
        onContinue={continueTutorial!}
        onRestart={restartTutorial!}
      />
    </>
  );
};

function getContainerCss(gameType: GameTypeEnum, buttonsCount: number) {
  if (gameType === GameTypeEnum.TOURNAMENT_GAME) {
    return css``;
  } else {
    return css({
      display: 'grid',
      gridTemplateColumns: `repeat(${buttonsCount || 1}, ${
        buttonsCount === 1 ? '0.5' : '1'
      }fr)`,
      columnGap: 15,
      justifyContent: 'center',
    });
  }
}

const ButtonCss = css({
  width: '100%',
});

export default StartGameButtonsWithoutTraining;
